import { signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import { notifyError } from '../../../../utils/Notify';
import { ERROR_TYPES, FIREBASE_STORAGE_KEYS } from '../utils/constantsNumberLogin';
import { handleFirebaseNumberLoginError, handleOTPVerificationError } from '../utils/errorHandlerNumberLogin';
import { getRemainingWaitTime, clearRateLimit } from './rateLimitService';
import { 
  generateUniqueRecaptchaId, 
  createHiddenContainer, 
  removeElementById,
  prepareCleanRecaptchaEnvironment,
  createRecaptchaVerifier,
  createRecaptchaStatusMonitor,
  createRecaptchaCallbacks,
  cleanupRecaptchaVerifier,
  setCurrentRecaptchaProcess,
  getCurrentRecaptchaProcess,
  clearCurrentRecaptchaProcess
} from '../../../../utils/recaptchaService';
import { 
  CLEANUP_DELAY_MS, 
  RECAPTCHA_TIMEOUT_MS, 
  STATUS_CHECK_INTERVAL_MS,
  RECAPTCHA_STATE_ACTIONS 
} from '../utils/constantsNumberLogin';
import { handleRecaptchaError } from '../utils/errorHandlerNumberLogin';

/**
 * Main service for phone number authentication
 * @param {Object} auth - Firebase authentication instance
 * @param {string} number - Phone number
 * @param {Function} setStep - Function to update step
 * @param {string} type - Authentication type
 * @param {string} id - Container ID
 * @param {Function|null} setLoadingCallback - Loading callback
 * @returns {Promise<Object>} Authentication result
 */
export const phoneNumberSignUp = async (auth, number, setStep, type, id, setLoadingCallback = null) => {
  let recaptchaState = null;
  
  try {
    const waitMinutes = getRemainingWaitTime();
    if (waitMinutes) {
      notifyError(`Veuillez attendre ${waitMinutes} minute(s) avant de réessayer.`);
      return { success: false, error: ERROR_TYPES.RATE_LIMITED };
    }

    console.log('Starting authentication for:', number);

    // Prepare reCAPTCHA environment
    await prepareCleanRecaptchaEnvironment(id, CLEANUP_DELAY_MS);
    
    const uniqueId = generateUniqueRecaptchaId();
    createHiddenContainer(uniqueId);

    // Initialize reCAPTCHA state
    recaptchaState = { resolved: false, cancelled: false };
    setCurrentRecaptchaProcess(recaptchaState);

    // Create reCAPTCHA verifier
    const callbacks = createRecaptchaCallbacks(recaptchaState, handleRecaptchaError);
    const recaptchaVerifier = createRecaptchaVerifier(uniqueId, auth, callbacks);

    console.log('Rendering reCAPTCHA...');
    window.recaptchaVerifier = recaptchaVerifier;
    await recaptchaVerifier.render();
    console.log('reCAPTCHA rendered successfully');

    if (setLoadingCallback) {
      setTimeout(() => {
        setLoadingCallback(false);
      }, 7000);
    }

    const statusMonitor = createRecaptchaStatusMonitor(recaptchaState, {
      stateActions: RECAPTCHA_STATE_ACTIONS,
      statusCheckInterval: STATUS_CHECK_INTERVAL_MS,
      timeout: RECAPTCHA_TIMEOUT_MS
    });
    const authPromise = signInWithPhoneNumber(auth, number, recaptchaVerifier);

    const confirmationResult = await Promise.race([
      authPromise.catch(error => {
        recaptchaState.cancelled = true;
        throw error;
      }),
      statusMonitor.then(() => authPromise)
    ]);

    window.confirmationResult = confirmationResult;
    console.log('SMS sent successfully');
    
    clearRateLimit();
    removeElementById(uniqueId);
    
    if (getCurrentRecaptchaProcess() === recaptchaState) {
      clearCurrentRecaptchaProcess();
    }
 
    
    return { success: true };

  } catch (error) {
    cleanupRecaptchaVerifier();
    
    if (recaptchaState && getCurrentRecaptchaProcess() === recaptchaState) {
      clearCurrentRecaptchaProcess();
    }
    
    return handleFirebaseNumberLoginError(error);
  }
};

/**
 * Service to verify OTP code
 * @param {Object} auth - Firebase authentication instance
 * @param {string} otp - OTP code to verify
 * @returns {Promise<Object>} Verification result
 */
export const verifyOTP = async (auth, otp) => {
  try {
    const credentials = PhoneAuthProvider.credential(
      window.confirmationResult.verificationId,
      otp,
    );
    credentials.user = await signInWithCredential(auth, credentials);
    
    cleanupRecaptchaVerifier();
    
    return {
      success: true,
      user: credentials.user || null,
    };
  } catch (err) {
    return handleOTPVerificationError(err);
  }
}; 